// decisions.js
// The process record, its decisions and their versions (Spec 2 section 1.3):
// the browser's twin of functions/ingestion/cleaning/records.py. Keep the two
// in step by hand. These helpers are the only frontend code that touches the
// inside of a record.
//
// A record is a cleaning process's entry on its owner, the file or the unit,
// under `processes[processId]`:
//
//     Record   = { decisions: [Decision], ran_at, edited_at, requested, requested_keys,
//                  saw: {...}, dropped: [{ key, reason, at }], summary: {...} | null }
//     Decision = { key: 'Gross_Premium' | { row: 14 }, versions: [Version] }   // oldest first
//     Version  = { value, refers_to, produced, made_by: 'process' | 'person',
//                  by: uid | 'decode', made_at, editable, active }
//
// A decision has at most one active version. The process's own proposal is
// its first version. A person's edit appends a version and deactivates the one
// before, so a reset can return to the proposal and the history stays.
//
// ran_at and edited_at are server timestamps on the record: a record is a map,
// where Firestore allows the serverTimestamp() sentinel, and a server clock on
// both sides means the "edited since it ran" comparison never meets clock
// skew. A version's made_at sits inside an array, where Firestore allows no
// sentinel, so it is the writer's clock, as on the backend.
//
// A Classify record whose decision list is too large for the unit document
// carries `decisions_path`, the blob holding the list, in place of `decisions`
// (Spec 2 section 4.4). Load that list onto the record (withDecisionList)
// before handing the record to the other helpers.
//
// Every function here reads plain objects and returns new ones; nothing it is
// given is changed.

import { FieldValue, serverTimestamp } from 'firebase/firestore';

// The two requests a person can make of a process: run it again keeping every
// decision that still refers ('reapply'), or propose afresh ('start_over').
export const REQUEST_KINDS = ['reapply', 'start_over'];

// A record for a process that has never run: pending by construction.
export function newRecord() {
  return {
    decisions: [],
    ran_at: null,
    edited_at: null,
    requested: null,
    requested_keys: null,
    saw: {},
    dropped: [],
    summary: null,
  };
}

// Whether two decision keys name the same thing. A key is text when the thing
// has a name (a sheet, a column, a unit id) and a small flat map when it is a
// tuple (a row: { row: 14, period: 'Feb-25' }). A map is compared by its
// entries, whatever their order, as records.hashable_key compares them.
export function sameKey(first, second) {
  const firstIsMap = first !== null && typeof first === 'object';
  const secondIsMap = second !== null && typeof second === 'object';
  if (!firstIsMap || !secondIsMap) return first === second;
  const firstNames = Object.keys(first).sort();
  const secondNames = Object.keys(second).sort();
  if (firstNames.length !== secondNames.length) return false;
  return firstNames.every((name, position) => (
    name === secondNames[position] && first[name] === second[name]
  ));
}

export function findDecision(record, key) {
  const decision = record.decisions.find((candidate) => sameKey(candidate.key, key));
  return decision === undefined ? null : decision;
}

export function activeVersion(record, key) {
  const decision = findDecision(record, key);
  if (decision === null) return null;
  const version = decision.versions.find((candidate) => candidate.active);
  return version === undefined ? null : version;
}

export function activeValue(record, key) {
  const version = activeVersion(record, key);
  return version === null ? null : version.value;
}

// The versions of the decision under `key`, oldest first, active or not: the
// process's own proposal first when there is one. Empty when the record has
// no decision under the key.
export function versionsOf(record, key) {
  const decision = findDecision(record, key);
  return decision === null ? [] : [...decision.versions];
}

// The active version of every decision that has one, in the record's order, as
// [{ key, version }]. (records.active_decisions answers a dict by key; a list
// keeps a row key as the map it is.)
export function activeDecisions(record) {
  const active = [];
  for (const decision of record.decisions) {
    const version = decision.versions.find((candidate) => candidate.active);
    if (version !== undefined) active.push({ key: decision.key, version });
  }
  return active;
}

// Every version of every decision, active or not, as [{ key, version }]: the
// decisions in the record's order, each one's versions oldest first.
export function everyVersion(record) {
  const versions = [];
  for (const decision of record.decisions) {
    for (const version of decision.versions) versions.push({ key: decision.key, version });
  }
  return versions;
}

// The blob path of a record whose decision list is stored outside it, or
// null when the record holds its list itself.
export function spilledListPath(record) {
  return record.decisions_path == null ? null : record.decisions_path;
}

// The decision list a record holds itself, as it is stored: what a spilled
// list's blob holds.
export function decisionListOf(record) {
  return record.decisions;
}

// The record holding `decisions` as its own list, in place of any blob path:
// a spilled list once it has been read, or a changed list small enough to
// keep on the unit document.
export function withDecisionList(record, decisions) {
  const withoutList = { ...record };
  delete withoutList.decisions_path;
  return { ...withoutList, decisions };
}

// The record naming the blob at `path` as where its decision list is stored,
// in place of the list itself.
export function withSpilledList(record, path) {
  const withoutList = { ...record };
  delete withoutList.decisions;
  return { ...withoutList, decisions_path: path };
}

// The record with a person's version appended to the decision under `key`,
// active, every earlier version of it deactivated. The decision is created
// when the record has none under that key. `refersTo` is what the value
// depends on: a document row's base ({ base }), or a class choice's mapped
// source column ({ column }), without which the process drops the version at
// its next run.
//
// stampEdit stamps the record's edited_at, which makes the process pending.
// Approval's Map freeze passes false: freezing a mapping is not an edit.
export function setPersonVersion(record, key, value, by, { refersTo = null, stampEdit = true } = {}) {
  const version = {
    value,
    refers_to: refersTo,
    produced: null,
    made_by: 'person',
    by,
    made_at: new Date(),
    editable: true,
    active: true,
  };
  const existing = findDecision(record, key);
  let decisions;
  if (existing === null) {
    decisions = [...record.decisions, { key, versions: [version] }];
  } else {
    decisions = record.decisions.map((decision) => {
      if (decision !== existing) return decision;
      const earlier = decision.versions.map((earlierVersion) => ({ ...earlierVersion, active: false }));
      return { ...decision, versions: [...earlier, version] };
    });
  }
  const edited = { ...record, decisions };
  if (stampEdit) edited.edited_at = serverTimestamp();
  return edited;
}

// The record with the first version of the decision under `key` active again,
// the process's own proposal when there is one, and the edit stamped.
export function resetDecision(record, key) {
  const existing = findDecision(record, key);
  if (existing === null || existing.versions.length === 0) {
    throw new Error('There is nothing to undo here.');
  }
  const decisions = record.decisions.map((decision) => {
    if (decision !== existing) return decision;
    const versions = decision.versions.map((version, position) => ({ ...version, active: position === 0 }));
    return { ...decision, versions };
  });
  return { ...record, decisions, edited_at: serverTimestamp() };
}

// The record with every decision left without an active version, except the
// decisions under `keptKeys`. A start-over on a unit process shows its
// decisions gone at once (Spec 2 section 6.1) and keeps a rule's 'definition',
// which the run remakes. The versions stay beneath, as records.deactivate
// leaves them.
export function deactivateDecisions(record, keptKeys = []) {
  const decisions = record.decisions.map((decision) => {
    if (keptKeys.some((kept) => sameKey(kept, decision.key))) return decision;
    const versions = decision.versions.map((version) => ({ ...version, active: false }));
    return { ...decision, versions };
  });
  return { ...record, decisions };
}

// The record carrying a person's request that its process run again. With
// 'start_over', `keys` limits the fresh proposals to those decisions (Segment's
// "Read this sheet again"); without keys the whole record is proposed afresh.
export function requestRun(record, kind, keys = null) {
  if (!REQUEST_KINDS.includes(kind)) {
    throw new Error(`A request is 'reapply' or 'start_over', not ${JSON.stringify(kind)}.`);
  }
  return { ...record, requested: kind, requested_keys: keys };
}

// How many of the record's decisions a person's version decides now: the
// "{n} changes by you" chip on a step.
export function personChangeCount(record) {
  return activeDecisions(record).filter(({ version }) => version.made_by === 'person').length;
}

// Whether the record carries work submitted and not yet run: a request, or an
// edit newer than its last run (statuses.record_is_waiting). A record that has
// never run is waiting once it is edited.
export function isPending(record) {
  if (record.requested) return true;
  if (record.edited_at == null) return false;
  // The browser's own edit, not yet written: the server will stamp it later
  // than any run that has already finished.
  if (record.edited_at instanceof FieldValue) return true;
  if (record.ran_at == null) return true;
  // Firestore Timestamps define valueOf so that > orders them, seconds then
  // nanoseconds, as it orders two Dates.
  return record.edited_at > record.ran_at;
}

// How the review surface writes a row key: 'row 14', 'row 12, Feb-25' for an
// unpivoted row, 'p3 #4' for a document row (records.row_key_text).
export function rowKeyText(key) {
  if ('page' in key) return `p${key.page} #${key.record}`;
  if ('period' in key) return `row ${key.row}, ${key.period}`;
  return `row ${key.row}`;
}
